import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { AumicoCoreProvider, NotificationType } from '../../../../aumico-lib-core-fe/src/lib';
import { ProjectsOverviewActions } from '../../../../aumico-lib-projects-fe/src/lib';
import { setAnnotations } from '../actions/annotations.actions';
import { TasksOverviewActions } from '../actions/tasks.actions';
import { GetProjectsTasksDto } from '../dtos/getProjectTasksDto.interface';
import { GetTasksOfProjectDto } from '../dtos/getTasksOfProjectDto';
import { TasksService } from '../services/tasks.service';

@Injectable()
export class TasksEffects {
    constructor(
        private actions$: Actions,
        private tasksService: TasksService,
        private coreProvider: AumicoCoreProvider,
        private store: Store,
    ) { }

    loadTasksOverview$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TasksOverviewActions.loadTasksOverview),
            mergeMap((action) => {
                return this.tasksService.getUserProjectsTasks(action.filter).pipe(
                    map((response: GetProjectsTasksDto) => TasksOverviewActions.tasksOverviewLoaded({ response })),
                    catchError((error) => of(TasksOverviewActions.tasksOverviewLoadFailure({ error }))),
                );
            }),
        ),
    );
    loadProjectTasks$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TasksOverviewActions.loadProjectTasks),
            mergeMap((action) => {
                return this.tasksService.getProjectsTasks(action.projectId, action.filter, action.subjectType).pipe(
                    mergeMap((response: GetTasksOfProjectDto) => [
                        TasksOverviewActions.projectTasksLoaded({ response }),
                        setAnnotations({ payload: response.annotations }),
                    ]),
                    catchError((error) => of(TasksOverviewActions.projectTasksLoadFailure({ error }))),
                );
            }),
        ),
    );

    createTask$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TasksOverviewActions.createTask),
            mergeMap((action) =>
                this.tasksService.createTask(action.projectId, action.createTaskRequestDto, action.subjectType).pipe(
                    map((response) => {
                        this.coreProvider.dispatchAddNotification(
                            'aumico_cpmm_task_create_success_title',
                            'aumico_cpmm_task_create_success_description',
                            NotificationType.SUCCESS,
                        );
                        return TasksOverviewActions.taskCreated({ response });
                    }),
                    catchError((error) => {
                        this.coreProvider.dispatchAddNotification(
                            'aumico_cpmm_task_create_failure_title',
                            'aumico_cpmm_task_create_failure_description',
                            NotificationType.ERROR,
                        );
                        return of(TasksOverviewActions.taskCreateFailure({ error }));
                    }),
                ),
            ),
        ),
    );

    updateTask$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TasksOverviewActions.updateTask),
            mergeMap((action) =>
                this.tasksService.updateTask(action.taskId, action.updateTaskDto).pipe(
                    map((response) => {
                        this.coreProvider.dispatchAddNotification(
                            'aumico_cpmm_task_update_success_title',
                            'aumico_cpmm_task_update_success_description',
                            NotificationType.SUCCESS,
                        );
                        return TasksOverviewActions.taskUpdated({ response });
                    }),
                    catchError((error) => {
                        this.coreProvider.dispatchAddNotification(
                            'aumico_cpmm_task_update_failure_title',
                            'aumico_cpmm_task_update_failure_description',
                            NotificationType.ERROR,
                        );
                        return of(TasksOverviewActions.taskUpdateFailure({ error }));
                    }),
                ),
            ),
        ),
    );

    updateTaskStatusForProjectByTaskId$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TasksOverviewActions.updateTaskStatusForProjectByTaskId),
            mergeMap((action) =>
                this.tasksService.updateTask(action.taskId, action.updateTaskDto).pipe(
                    map((response) => {
                        this.store.dispatch(
                            ProjectsOverviewActions.updateProjectWorkflowdataForTask({
                                projectId: action.projectId,
                                taskId: action.taskId,
                                task: response,
                            }),
                        );
                        this.coreProvider.dispatchAddNotification(
                            'aumico_cpmm_task_update_success_title',
                            'aumico_cpmm_task_update_success_description',
                            NotificationType.SUCCESS,
                        );
                        return TasksOverviewActions.taskStatusForProjectByTaskIdUpdated({
                            task: response,
                            projectId: action.projectId,
                            taskId: action.taskId,
                        });
                    }),
                    catchError((error) => {
                        this.coreProvider.dispatchAddNotification(
                            'aumico_cpmm_task_update_failure_title',
                            'aumico_cpmm_task_update_failure_description',
                            NotificationType.ERROR,
                        );
                        return of(TasksOverviewActions.taskStatusForProjectByTaskIdUpdateFailure({ error }));
                    }),
                ),
            ),
        ),
    );

    updateTasksStatusForProjectBySectionId$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TasksOverviewActions.updateAllTasksStatusForProjectBySectionId),
            mergeMap((action) =>
                this.tasksService
                    .updateAllTasksStatusForProjectBySectionId(action.projectId, action.sectionId, action.updateTaskDto)
                    .pipe(
                        map((response) => {
                            this.store.dispatch(
                                ProjectsOverviewActions.updateWorkflowdataForAllTasksBySectionId({
                                    projectId: action.projectId,
                                    sectionId: action.sectionId,
                                    tasks: response,
                                }),
                            );

                            this.coreProvider.dispatchAddNotification(
                                'aumico_cpmm_task_update_success_title',
                                'aumico_cpmm_task_update_success_description',
                                NotificationType.SUCCESS,
                                2000,
                            );
                            return TasksOverviewActions.allTasksStatusForProjectBySectionIdUpdated({
                                tasks: response,
                                projectId: action.projectId,
                                sectionId: action.sectionId,
                            });
                        }),
                        catchError((error) => {
                            this.coreProvider.dispatchAddNotification(
                                'aumico_cpmm_task_update_failure_title',
                                'aumico_cpmm_task_update_failure_description',
                                NotificationType.ERROR,
                                2000,
                            );
                            return of(TasksOverviewActions.allTasksStatusForProjectBySectionIdUpdateFailure({ error }));
                        }),
                    ),
            ),
        ),
    );

    bulkUpdateTasks$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TasksOverviewActions.bulkUpdateTasks),
            mergeMap((action) =>
                this.tasksService.bulkUpdateTasks(action.taskIds, action.updateTaskDto).pipe(
                    map((response) => {
                        this.coreProvider.dispatchAddNotification(
                            'aumico_cpmm_task_bulk_update_success_title',
                            'aumico_cpmm_task_bulk_update_success_description',
                            NotificationType.SUCCESS,
                        );
                        return TasksOverviewActions.bulkTasksUpdated({ response });
                    }),
                    catchError((error) => {
                        this.coreProvider.dispatchAddNotification(
                            'aumico_cpmm_task_bulk_update_failure_title',
                            'aumico_cpmm_task_bulk_update_failure_description',
                            NotificationType.ERROR,
                        );
                        return of(TasksOverviewActions.bulkUpdateTasksFailure({ error }));
                    }),
                ),
            ),
        ),
    );

    deleteTask$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TasksOverviewActions.deleteTask),
            mergeMap((action) =>
                this.tasksService.deleteTask(action.taskId).pipe(
                    map(() => {
                        this.coreProvider.dispatchAddNotification(
                            'aumico_cpmm_task_delete_success_title',
                            'aumico_cpmm_task_delete_success_description',
                            NotificationType.SUCCESS,
                        );
                        return TasksOverviewActions.taskDeleted({ taskId: action.taskId });
                    }),
                    catchError((error) => {
                        this.coreProvider.dispatchAddNotification(
                            'aumico_cpmm_task_delete_failure_title',
                            'aumico_cpmm_task_delete_failure_description',
                            NotificationType.ERROR,
                        );
                        return of(TasksOverviewActions.taskDeleteFailure({ error }));
                    }),
                ),
            ),
        ),
    );

    bulkDeleteTasks$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TasksOverviewActions.bulkDeleteTasks),
            mergeMap((action) =>
                this.tasksService.bulkDeleteTasks(action.taskIds).pipe(
                    map(() => {
                        this.coreProvider.dispatchAddNotification(
                            'aumico_cpmm_task_bulk_delete_success_title',
                            'aumico_cpmm_task_bulk_delete_success_description',
                            NotificationType.SUCCESS,
                        );
                        return TasksOverviewActions.bulkTasksDeleted({ taskIds: action.taskIds });
                    }),
                    catchError((error) => {
                        this.coreProvider.dispatchAddNotification(
                            'aumico_cpmm_task_bulk_delete_failure_title',
                            'aumico_cpmm_task_bulk_delete_failure_description',
                            NotificationType.ERROR,
                        );
                        return of(TasksOverviewActions.bulkDeleteTasksFailure({ error }));
                    }),
                ),
            ),
        ),
    );
}
